import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const AUTHED_TEMPLATES_URL = '/document-builder/templates';
const PUBLIC_TEMPLATES_URL = '/public/templates';

// ============ Types ============

interface SlideTemplate {
    slideType: string;
    variant: string | null;
    thumbnailUrl?: string | null;
}

export interface DocumentTemplate {
    docxUrl: string;
    thumbnailUrl: string;
    originalFileName: string;
}

export interface TemplateItem {
    _id: string;
    templateId?: string; // Some API responses use templateId
    name: string;
    slug: string;
    description: string;
    category: string;
    thumbnailUrl: string | null;
    docxUrl?: string | null; // Top-level docxUrl from API
    applicablePresentationTypes?: string[];
    slideTemplates: SlideTemplate[];
    documentTemplate?: DocumentTemplate;
    source?: 'user' | 'platform';
}

interface GetTemplatesParams {
    offset?: number;
    limit?: number;
    search?: string;
    documentType?: string;
    isAuthenticated?: boolean;
}

export interface GetTemplatesResponse {
    statusCode: number;
    data: {
        templates: TemplateItem[];
        pagination: {
            total: number;
            offset: number;
            limit: number;
            hasMore: boolean;
        };
    };
}

interface CreateUserTemplateRequest {
    name: string;
    file: File;
    description?: string;
    documentType?: string;
}

// ============ API Definition ============

export const templateApi = createApi({
    reducerPath: 'templateApi',
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.NEXT_PUBLIC_API_URL,
        prepareHeaders: (headers, { getState }) => {
            const token = (getState() as any).auth.token;
            if (token) {
                headers.set('authorization', `Bearer ${token}`);
            }
            return headers;
        },
    }),
    tagTypes: ['Templates'],
    endpoints: (builder) => ({
        getTemplates: builder.query<GetTemplatesResponse, GetTemplatesParams>({
            query: ({ offset = 0, limit = 12, search, documentType, isAuthenticated } = {}) => ({
                url: isAuthenticated ? AUTHED_TEMPLATES_URL : PUBLIC_TEMPLATES_URL,
                params: {
                    offset,
                    limit,
                    ...(search ? { search } : {}),
                    ...(documentType ? { documentType } : {}),
                },
            }),
            providesTags: ['Templates'],
        }),

        getUserTemplate: builder.query<TemplateItem, string>({
            query: (templateId) => `/document-builder/user-templates/${templateId}`,
            providesTags: (result, error, id) => [{ type: 'Templates', id }],
        }),

        createUserTemplate: builder.mutation<TemplateItem, CreateUserTemplateRequest>({
            query: (body) => {
                const formData = new FormData();
                formData.append('file', body.file);
                formData.append('name', body.name);
                if (body.description) formData.append('description', body.description);
                if (body.documentType) formData.append('documentType', body.documentType);
                
                return {
                    url: '/document-builder/user-templates',
                    method: 'POST',
                    body: formData,
                };
            },
            invalidatesTags: ['Templates'],
        }),

        updateUserTemplate: builder.mutation<TemplateItem, { templateId: string; name?: string; description?: string; documentType?: string }>({
            query: ({ templateId, ...body }) => ({
                url: `/document-builder/user-templates/${templateId}`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error, { templateId }) => [{ type: 'Templates', id: templateId }, 'Templates'],
        }),

        deleteUserTemplate: builder.mutation<{ success: boolean }, string>({
            query: (templateId) => ({
                url: `/document-builder/user-templates/${templateId}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Templates'],
        }),
    }),
});

export const { 
    useGetTemplatesQuery, 
    useLazyGetTemplatesQuery,
    useGetUserTemplateQuery,
    useCreateUserTemplateMutation,
    useUpdateUserTemplateMutation,
    useDeleteUserTemplateMutation,
} = templateApi;
